/**
 * Service de gestion des comptes bancaires (web)
 * Solde = ancrage validé + transactions
 */

import { supabase } from '../supabaseClient';
import { getImpersonatedUserIdFromStorage } from '../../contexts/ImpersonationContext';

export interface Compte {
  id?: string;
  user_id?: string;
  nom: string;
  description?: string;
  solde: number;
  solde_initial?: number;
  couleur: string;
  type?: 'compte_courant' | 'epargne' | 'credit' | 'autre';
  iban?: string;
  bic?: string;
  numero_compte?: string;
  nom_banque?: string;
  /** URL du portail de connexion de la banque */
  site_web?: string | null;
  actif?: boolean;
  /** Compte utilisé par défaut (import CSV, etc.) — un seul par utilisateur */
  is_default?: boolean;
  commentaire?: string;
  /** Icône image (bucket transaction-attachments, dossier comptes/) */
  attachment_path?: string | null;
  created_at?: string;
  updated_at?: string;
  currency?: string;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export const getComptes = async (): Promise<{ data: Compte[] | null; error: any }> => {
  try {
    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    if (impersonatedUserId) {
      const rpc = await supabase.rpc('admin_get_comptes', { p_user_id: impersonatedUserId });
      if (rpc.error) return { data: null, error: rpc.error };
      const rows = (rpc.data as any[]) || [];
      // Enrichir avec solde calculé si possible
      const enriched = await Promise.all(
        rows.map(async (r) => {
          const { data: bal } = await supabase.rpc('calculate_compte_balance', {
            p_compte_id: r.id,
            p_target_date: todayISO(),
          });
          return { ...r, solde: Number(bal ?? r.solde ?? 0) } as Compte;
        }),
      );
      return { data: enriched, error: null };
    }

    const { data, error } = await supabase.rpc('get_my_comptes_with_balance', {
      p_target_date: todayISO(),
    });

    if (error) {
      console.error('get_my_comptes_with_balance fallback:', error);
      const res = await supabase.from('comptes').select('*').order('created_at', { ascending: true });
      if (res.error) return { data: null, error: res.error };
      return { data: (res.data as Compte[]) || [], error: null };
    }

    const rows = (data as any[]) || [];
    return {
      data: rows.map((r) => ({
        id: r.id,
        user_id: r.user_id,
        nom: r.nom,
        description: r.description ?? undefined,
        solde: Number(r.solde_calcule ?? r.solde ?? 0),
        solde_initial: r.solde_initial != null ? Number(r.solde_initial) : undefined,
        couleur: r.couleur,
        commentaire: r.commentaire,
        currency: r.currency || 'EUR',
        type: r.type,
        actif: r.actif !== false,
        is_default: r.is_default === true,
        attachment_path: r.attachment_path ?? null,
        site_web: r.site_web ?? null,
        iban: r.iban ?? undefined,
        bic: r.bic ?? undefined,
        numero_compte: r.numero_compte ?? undefined,
        nom_banque: r.nom_banque ?? undefined,
        created_at: r.created_at,
        updated_at: r.updated_at,
      })),
      error: null,
    };
  } catch (error) {
    console.error('Exception getComptes:', error);
    return { data: null, error };
  }
};

export const createCompte = async (
  compte: Compte,
): Promise<{ data: Compte | null; error: any }> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { data: null, error: 'Utilisateur non connecté' };

    const soldeDepart = Number(compte.solde ?? 0);
    const { is_default: wantDefault, ...compteRest } = compte;
    const { data, error } = await supabase
      .from('comptes')
      .insert([
        {
          ...compteRest,
          is_default: false,
          solde: soldeDepart,
          solde_initial: compte.solde_initial ?? soldeDepart,
          user_id: user.id,
        },
      ])
      .select()
      .single();

    if (error) {
      console.error('Erreur création compte:', error);
      return { data: null, error };
    }

    if (data?.id) {
      await supabase.rpc('create_balance_anchor_point', {
        p_compte_id: data.id,
        p_date_solde: todayISO(),
        p_solde_validé: soldeDepart,
        p_commentaire: 'Solde de départ à la création du compte',
      });
      if (wantDefault) {
        await supabase.rpc('set_compte_as_default', { p_compte_id: data.id });
      }
    }

    return {
      data: { ...(data as Compte), solde: soldeDepart, is_default: !!wantDefault },
      error: null,
    };
  } catch (error) {
    console.error('Exception createCompte:', error);
    return { data: null, error };
  }
};

export const updateCompte = async (
  id: string,
  updates: Partial<Compte>,
): Promise<{ data: Compte | null; error: any }> => {
  try {
    const payload: Record<string, unknown> = { ...updates };
    const soldeAdjust =
      updates.solde !== undefined ? Number(updates.solde) : undefined;
    const wantDefault = updates.is_default === true;
    delete payload.solde; // géré via ancrage
    // is_default géré via RPC pour garantir l’unicité
    if ('is_default' in payload) delete payload.is_default;

    const { data, error } = await supabase
      .from('comptes')
      .update(payload)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erreur updateCompte:', error);
      return { data: null, error };
    }

    if (wantDefault) {
      const { error: defErr } = await supabase.rpc('set_compte_as_default', {
        p_compte_id: id,
      });
      if (defErr) {
        console.error('Erreur set_compte_as_default:', defErr);
        return { data: data as Compte, error: defErr };
      }
    } else if (updates.is_default === false) {
      await supabase.from('comptes').update({ is_default: false }).eq('id', id);
    }

    if (soldeAdjust !== undefined && !Number.isNaN(soldeAdjust)) {
      const adj = await adjustCompteSolde(id, soldeAdjust, {
        commentaire: 'Ajustement manuel du solde (web)',
      });
      if (!adj.success) {
        return { data: data as Compte, error: adj.error };
      }
      return {
        data: { ...(data as Compte), solde: adj.solde ?? soldeAdjust },
        error: null,
      };
    }

    return { data: data as Compte, error: null };
  } catch (error) {
    console.error('Exception updateCompte:', error);
    return { data: null, error };
  }
};

export const adjustCompteSolde = async (
  compteId: string,
  soldeValide: number,
  opts?: { date?: string; commentaire?: string },
): Promise<{ success: boolean; solde?: number; error: any }> => {
  try {
    const { error } = await supabase.rpc('create_balance_anchor_point', {
      p_compte_id: compteId,
      p_date_solde: opts?.date || todayISO(),
      p_solde_validé: soldeValide,
      p_commentaire: opts?.commentaire ?? 'Ajustement manuel du solde',
    });
    if (error) return { success: false, error };

    const { data: soldeCalcule } = await supabase.rpc('calculate_compte_balance', {
      p_compte_id: compteId,
      p_target_date: todayISO(),
    });

    return { success: true, solde: Number(soldeCalcule ?? soldeValide), error: null };
  } catch (error) {
    return { success: false, error };
  }
};

export const deleteCompte = async (id: string): Promise<{ success: boolean; error: any }> => {
  try {
    const { error } = await supabase.from('comptes').delete().eq('id', id);
    if (error) return { success: false, error };
    return { success: true, error: null };
  } catch (error) {
    return { success: false, error };
  }
};

export const getCompteById = async (id: string): Promise<{ data: Compte | null; error: any }> => {
  try {
    const { data, error } = await supabase.from('comptes').select('*').eq('id', id).single();
    if (error) return { data: null, error };
    const { data: bal } = await supabase.rpc('calculate_compte_balance', {
      p_compte_id: id,
      p_target_date: todayISO(),
    });
    return {
      data: { ...(data as Compte), solde: Number(bal ?? (data as Compte).solde ?? 0) },
      error: null,
    };
  } catch (error) {
    return { data: null, error };
  }
};

/** @deprecated Utiliser adjustCompteSolde */
export const updateCompteSolde = async (
  id: string,
  nouveauSolde: number,
): Promise<{ data: Compte | null; error: any }> => {
  const adj = await adjustCompteSolde(id, nouveauSolde);
  if (!adj.success) return { data: null, error: adj.error };
  return getCompteById(id);
};
